import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';

/**
 * Camera barcode scanner modal.
 *
 * Prefers the native `BarcodeDetector` API when the browser exposes it, and
 * falls back to `@zxing/browser` otherwise. Calls `onDetected(code)` with the
 * first decoded EAN/UPC, then the parent closes the modal. This component owns
 * a live camera stream and is intentionally excluded from the jsdom unit tests.
 */

interface BarcodeScannerProps {
  onDetected: (code: string) => void;
  onClose: () => void;
}

interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
}

const BARCODE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128'];

export default function BarcodeScanner({ onDetected, onClose }: BarcodeScannerProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let raf = 0;
    let zxingControls: { stop: () => void } | null = null;

    function done(code: string): void {
      if (stopped) return;
      stopped = true;
      onDetected(code);
    }

    async function start(): Promise<void> {
      const video = videoRef.current;
      if (!video) return;

      // Native BarcodeDetector path.
      const Detector = (window as unknown as { BarcodeDetector?: new (o: unknown) => BarcodeDetectorLike })
        .BarcodeDetector;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
      } catch {
        setError('Camera unavailable. Grant permission or enter the barcode manually.');
        return;
      }
      if (stopped) return;
      video.srcObject = stream;
      await video.play().catch(() => {});

      if (Detector) {
        const detector = new Detector({ formats: BARCODE_FORMATS });
        const tick = async (): Promise<void> => {
          if (stopped) return;
          try {
            const codes = await detector.detect(video);
            if (codes[0]?.rawValue) return done(codes[0].rawValue);
          } catch {
            // transient decode error; keep scanning
          }
          raf = requestAnimationFrame(() => void tick());
        };
        void tick();
        return;
      }

      // @zxing/browser fallback.
      try {
        const reader = new BrowserMultiFormatReader();
        zxingControls = await reader.decodeFromVideoElement(video, (result) => {
          if (result) done(result.getText());
        });
      } catch {
        setError('Could not start the barcode reader. Enter the barcode manually.');
      }
    }

    void start();

    return () => {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      zxingControls?.stop();
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onDetected]);

  return (
    <div className="scanner" role="dialog" aria-label="Scan a barcode">
      <div className="scanner__frame">
        <video ref={videoRef} className="scanner__video" muted playsInline />
        <div className="scanner__reticle" />
      </div>
      {error ? (
        <p className="scanner__error">{error}</p>
      ) : (
        <p className="scanner__hint">Point the camera at a product barcode.</p>
      )}
      <button className="btn btn--ghost" onClick={onClose}>
        Cancel
      </button>
    </div>
  );
}
